import { auth, functionsBaseUrl } from "../../firebase";
import { CartServiceAddItemRequestType, CartServiceAddItemResponseType, CartServiceClearCartRequestType, CartServiceClearCartResponseType, CartServiceCollectOrderRequestType, CartServiceCollectOrderResponseType, CartServiceRefundDecisionRequestType, CartServiceRefundDecisionResponseType, CartServiceRefundOrderRequestType, CartServiceRefundOrderResponseType, CartServiceRemoveItemRequestType, CartServiceTransformTemporaryMenuCartToFullCartRequestType, CartServiceTransformTemporaryMenuCartToFullCartResponseType, OrderDecisionRequestType, OrderDecisionResponseType, PollPurchaseStatusRequestType, PurchaseCartRequestType, PurchaseCartResponseType } from "../../models/CartServiceType";
import { CartStatus, CartType } from "../../models/CartType";
import { SaleItem } from "../../models/saleItem";
import { yocoCheckoutsResponse } from "../../models/yocoCheckoutRequests";
import { FirestoreService } from "../firebase/firestoreService";

const CARTS_COLLECTION_NAME = "carts";

export const CartService = {
  fetchCart: async (): Promise<CartType | null> => {
    if (!auth.currentUser) {
      return null;
    }
    const customerId: string = auth.currentUser.uid;
    const cart = await FirestoreService.getDocument(CARTS_COLLECTION_NAME, customerId)
    return cart as CartType; // Convert Firestore data to FullProperty instance
  },

  transformTemporaryMenuCartToFullCart: async (): Promise<CartServiceTransformTemporaryMenuCartToFullCartResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Customer Not Signed In" };
    }
    const idToken: string = await auth.currentUser.getIdToken()
    const body: CartServiceTransformTemporaryMenuCartToFullCartRequestType = {}

    const response = await fetch(`${functionsBaseUrl}/transformTemporaryMenuCartToFullCart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    console.log(response)
    return (await response.json() as CartServiceTransformTemporaryMenuCartToFullCartResponseType);
  },

  clearCart: async (): Promise<CartServiceClearCartResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Customer Not Signed In" };
    }
    const idToken: string = await auth.currentUser.getIdToken()
    const body: CartServiceClearCartRequestType = {}

    const response = await fetch(`${functionsBaseUrl}/clearCart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    console.log(response)
    return (await response.json() as CartServiceClearCartResponseType);
  },

  addItemToCart: async (quantity: number, restaurantId: string, saleItem: SaleItem): Promise<CartServiceAddItemResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Customer Not Signed In" };
    }
    const idToken: string = await auth.currentUser.getIdToken()
    const body: CartServiceAddItemRequestType = {
      quantity,
      restaurantId,
      saleItemId: saleItem.id
    }

    const response = await fetch(`${functionsBaseUrl}/addItemToCart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    console.log(response)
    return (await response.json() as CartServiceAddItemResponseType);
  },

  removeItemFromCart: async (restaurantId: string, saleItem: SaleItem): Promise<CartType | null> => {
    if (!auth.currentUser) {
      return null;
    }
    const idToken: string = await auth.currentUser.getIdToken()
    const body: CartServiceRemoveItemRequestType = {
      restaurantId,
      saleItemId: saleItem.id
    }

    const response = await fetch(`${functionsBaseUrl}/removeItemFromCart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    console.log(response)
    if (response.status === 200) {
      return (await response.json())["cart"] as CartType;
    }
    return null;
  },

  purchaseCart: async (): Promise<PurchaseCartResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Customer Not Signed In" };
    }
    const idToken: string = await auth.currentUser.getIdToken()
    const body: PurchaseCartRequestType = {}

    const response = await fetch(`${functionsBaseUrl}/purchaseCart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    console.log(response)
    const yocoResponse = (await response.json()) as PurchaseCartResponseType;
    return yocoResponse;
  },

  pollPurchaseStatus: async (): Promise<CartStatus | null> => {
    if (!auth.currentUser) {
      return null;
    }
    const idToken: string = await auth.currentUser.getIdToken()
    const body: PollPurchaseStatusRequestType = {}

    const response = await fetch(`${functionsBaseUrl}/pollPurchaseStatus`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    console.log(response)
    if (response.status === 200) {
      const status = (await response.json());
      return status["status"];
    }
    if (response.status === 400) {
      const status = await response.json();
      if (status["error"] === "Cart Does Not Exist") {
        return null; // TODO: maybe we want to handle this somehow in the future likely the customer navigated back to the cart page
      }
    }
    return null;
  },
  cancelPendingOrder: async (): Promise<void> => {
    if (!auth.currentUser) {
      return;
    }
    const idToken: string = await auth.currentUser.getIdToken()

    const response = await fetch(`${functionsBaseUrl}/cancelPendingOrder`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      }
    });
    console.log(response)
    await response.json();
    return;
  },
  collectOrder: async (request: CartServiceCollectOrderRequestType): Promise<CartServiceCollectOrderResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Unauthorized" };
    }
    const idToken: string = await auth.currentUser.getIdToken()

    const response = await fetch(`${functionsBaseUrl}/collectOrder`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    console.log(response)
    return await response.json();
  },
  acceptRejectOrder: async (request: OrderDecisionRequestType): Promise<OrderDecisionResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Unauthorized" };
    }
    const idToken: string = await auth.currentUser.getIdToken()

    const response = await fetch(`${functionsBaseUrl}/acceptRejectOrder`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    console.log(response)
    return await response.json();
  },
  requestOrderRefund: async (request: CartServiceRefundOrderRequestType): Promise<CartServiceRefundOrderResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Unauthorized" };
    }
    const idToken: string = await auth.currentUser.getIdToken()

    const response = await fetch(`${functionsBaseUrl}/requestOrderRefund`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    console.log(response)
    return await response.json();
  },

  refundDecision: async (request: CartServiceRefundDecisionRequestType): Promise<CartServiceRefundDecisionResponseType> => {
    if (!auth.currentUser) {
      return { success: false, error: "Unauthorized" };
    }
    const idToken: string = await auth.currentUser.getIdToken()

    const response = await fetch(`${functionsBaseUrl}/refundDecision`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    console.log(response)
    return await response.json();
  },
};

